import { OpportunityPayload, OpportunityVisibility } from 'Event/interfaces'
import {
  EventGroup,
  OpportunityOccurrencePersonGroup,
  OpportunityResponseWithRecurrenceInfo,
  OpportunityStatus,
  RecurrenceInfo,
  TimeshiftsBase,
} from 'Event/interfaces/interfaceCreateEditEvent'
import { Geofencing } from 'civic-champs-shared/core/location/utils'
import {
  OpportunityTemplate,
  OpportunityTemplateCredential,
  OpportunityTemplatePersonGroup,
  OpportunityTemplateSurvey,
} from 'volunteering/opportunities/interfaces'
import {
  getWeekdayNumberInMonth,
  RECURRING_TYPE,
} from 'civic-champs-shared/core/utils/constants/RECURRING_TYPE_OPTIONS'
import { EventGeofencing, mapEventGeofencingToGeofencing, mapGeofencingToEventGeofencing } from 'utils/event'
import { DEFAULT_GEOFENCING } from 'civic-champs-shared/constants/GEO_DATA'
import { JoinQuestionSetPayload, JoinSurveyPayload } from 'civic-champs-shared/question-sets/types'
import { Role } from 'volunteer-role/types'
import { Person } from '../../people/interface'
import { LocationAddressType, OrganizationLocationReporting } from '../../locations/types'
import { mapLocationAddressFormToPayload } from '../../locations/helpers/location-address'
import { extractPlusCode } from '../../civic-champs-shared/core/location/plus-code-utils'
import {
  MONTHLY_RECURRING_TYPE,
  SIMPLE_RECURRING_TYPE,
} from '../../civic-champs-shared/core/utils/constants/SIMPLE_RECURRING_TYPE_OPTIONS'
import dayjs from '../../civic-champs-shared/core/utils/dayjs'
import last from 'lodash/last'
import first from 'lodash/first'
import isNull from 'lodash/isNull'
import omitBy from 'lodash/omitBy'
import pick from 'lodash/pick'
import { RRule, RRuleSet, rrulestr, Weekday } from 'rrule'
import isUndefined from 'lodash/isUndefined'
import { useDateTimeUtils } from 'civic-champs-shared/utils/useDateTimeUtils'
import { useFeatureEnabled } from 'civic-champs-shared/feature/hooks'
import { useTimeZone } from 'civic-champs-shared/auth/hooks'
import { isDate, map, uniqueId } from 'lodash'
import isEmpty from 'lodash/isEmpty'
import moment from 'moment'
import { useCallback } from 'react'

export interface FormRole {
  roleId: number | null
  quantity: number | null
  registrations: number
  itemId: string
}

export const createEmptyFormRole = (): FormRole => ({
  roleId: null,
  quantity: null,
  registrations: 0,
  itemId: uniqueId('role_'),
})

export const toFormRole = ({
  roleId,
  volunteeringRoleId,
  quantity,
  registrations = 0,
  itemId,
}: {
  roleId?: number | null
  volunteeringRoleId?: number | null
  quantity: number | null
  registrations?: number
  itemId?: string
}): FormRole => ({
  roleId: roleId ?? volunteeringRoleId ?? null,
  quantity,
  registrations,
  itemId: itemId ?? uniqueId('role_'),
})

export interface FormReferenceItem {
  id: number
  name: string
}

export interface FormData {
  name: string
  description: string
  address: string
  city: string
  state: string
  zip: string
  visibility: OpportunityVisibility
  isTest: boolean
  locationDetails: string
  locationIsAddress: boolean
  locationAddressType?: LocationAddressType
  startDate: Date | null
  endDate: Date | null
  startTime: Date
  endTime: Date
  dates: Date[]
  geofencing: Geofencing
  groups: EventGroup[]
  opportunityTemplate: OpportunityTemplate | null
  isSchedulable: boolean
  recurringType: RECURRING_TYPE
  roles: FormRole[]
  contactEmail: string
  contactName: string
  contactPhoneNumber: string
  instructions: string
  registrationMessage: string
  followUpEnabled: boolean
  followUpMessage: string
  followUpQuestionnaireIds: number[]
  questionnaires: FormReferenceItem[]
  waivers: FormReferenceItem[]
  timeShiftId?: number
  timeShiftName?: string
  editMode?: string
  organizationLocationId: number | null
  useTemplate?: boolean
  pointOfContactPersons?: Person[]
  useCustomAddress: boolean
  customAddressType?: LocationAddressType
  plusCode: string
  simpleRecurringType: SIMPLE_RECURRING_TYPE
  monthlyRecurringType: MONTHLY_RECURRING_TYPE
  isRecurring?: boolean
  recurrencePattern?: string
  useCustomPublishTime?: boolean
  publishDate?: Date
  publishTime?: Date
  allowMultipleRegistrations: boolean
}

export const getEffectiveAllowMultipleRegistrations = (
  allowMultipleRegistrationsPerVolunteerEnabled: boolean,
  allowMultipleRegistrations?: boolean,
) => allowMultipleRegistrationsPerVolunteerEnabled && !!allowMultipleRegistrations

export const getEventRegistrationsPolicyLabel = (allowMultipleRegistrations?: boolean) =>
  allowMultipleRegistrations ? 'Multiple per volunteer' : 'One per volunteer'

const mapGeofencingForLocationPayload = (
  geofencing: Geofencing,
  useCustomAddress: boolean,
  customAddressType?: LocationAddressType,
): EventGeofencing | null => {
  if (!useCustomAddress || customAddressType === LocationAddressType.CustomTextOnly) {
    return null
  }

  return mapGeofencingToEventGeofencing(geofencing)
}

const buildCustomLocationPayload = (
  values: Pick<
    FormData,
    'address' | 'city' | 'state' | 'zip' | 'plusCode' | 'locationDetails' | 'customAddressType' | 'geofencing'
  >,
) => {
  const mapped = mapLocationAddressFormToPayload({
    customAddressType: values.customAddressType,
    address: {
      line1: values.address,
      city: values.city,
      state: values.state,
      zip: values.zip,
      geofencing: values.geofencing,
    },
    plusCode: {
      line1: extractPlusCode(values.plusCode || ''),
      city: '',
      state: '',
      zip: '',
      geofencing: values.geofencing,
    },
    locationDetails: values.locationDetails,
  })

  const mappedLine1 = (mapped as { line1?: string | null }).line1

  return {
    address: mapped.locationIsAddress ? mappedLine1 ?? null : null,
    city:
      mapped.locationIsAddress && values.customAddressType === LocationAddressType.AddressAndCoords
        ? values.city
        : null,
    state:
      mapped.locationIsAddress && values.customAddressType === LocationAddressType.AddressAndCoords
        ? values.state
        : null,
    zip:
      mapped.locationIsAddress && values.customAddressType === LocationAddressType.AddressAndCoords ? values.zip : null,
    locationDetails: mapped.locationDetails,
    locationIsAddress: mapped.locationIsAddress,
    locationAddressType: mapped.locationAddressType,
  }
}

interface RecurrencePatternProps {
  startDate: Date
  endDate: Date
  dates: Date[]
  simpleRecurringType: SIMPLE_RECURRING_TYPE
  monthlyRecurringType: MONTHLY_RECURRING_TYPE
}

export interface EventPayload {
  id?: string | number
  name: string
  startsAt: Date
  endsAt: Date | null
  recurring_type?: number
  recurrencePattern?: string | null
  is_recurring: boolean
  dates: string[]
  description: string
  instructions: string
  registration_message?: string
  follow_up_enabled?: boolean
  follow_up_message?: string
  follow_up_questionnaire_ids?: number[]
  contact_name: string
  contact_email: string
  contact_phone_number: string
  address: string | null
  city: string | null
  state: string | null
  zip: string | null
  isTest: boolean
  geofencing: EventGeofencing | null
  questionSets?: (JoinQuestionSetPayload | JoinSurveyPayload)[] | null
  visibilityGroups?: EventGroup[]
  onboardingGroups?: EventGroup[]
  groups?: EventGroup[]
  requiredCredentials: {
    id: number
    title: string
  }[]
  visibility: OpportunityVisibility
  recurrenceInfo?: RecurrenceInfo
  occurrenceId?: number
  opportunityId?: number
  timeshifts: TimeshiftsBase[]
  locationDetails: string | null
  locationAddressType?: LocationAddressType
  locationIsAddress?: boolean
  isSchedulable: true
  asDraft?: boolean
  editMode?: string
  organizationLocationId?: number | null
  pointOfContactPersonIds?: number[]
  moveToDate?: Date
  forceVisibleAt?: Date | null
  allowMultipleRegistrations?: boolean
}

export const getFormValuesFromOpportunityTemplate = ({
  opportunityTemplate,
  opportunityTemplateGroups,
  opportunityTemplateSurveys,
  opportunityTemplateCredentials,
  namedLocations,
}: {
  opportunityTemplate?: OpportunityTemplate | null
  opportunityTemplateGroups: OpportunityTemplatePersonGroup[]
  opportunityTemplateSurveys: OpportunityTemplateSurvey[]
  opportunityTemplateCredentials: OpportunityTemplateCredential[]
  namedLocations: OrganizationLocationReporting[]
}): Partial<FormData> => {
  if (!opportunityTemplate) return {}
  const {
    name,
    //startTime,
    //endTime,
    //roles,
    streetAddress: address,
    geofencing,
    city,
    state,
    zip,
    visibility,
    locationDetails,
    locationIsAddress,
    locationAddressType,
    contactName,
    contactEmail,
    contactPhoneNumber,
    description,
    instructions,
    organizationLocationId,
    pointOfContactPersons,
  } = opportunityTemplate
  const customAddressType =
    locationAddressType ??
    (locationIsAddress
      ? LocationAddressType.AddressAndCoords
      : locationDetails
      ? LocationAddressType.CustomTextOnly
      : LocationAddressType.AddressAndCoords)
  const isPlusCode = customAddressType === LocationAddressType.PlusCode
  const values: Partial<FormData> = omitBy(
    {
      name,
      //startTime: toLocalDate(startTime), //TODO this is on the wrong day
      //endTime: toLocalDate(endTime),
      //roles: roles ? map(roles, ({ role, quantity }) => ({ volunteeringRoleId: role.id, quantity })) : null,
      geofencing: geofencing ? mapEventGeofencingToGeofencing(geofencing) : DEFAULT_GEOFENCING,
      address: isPlusCode ? '' : address,
      plusCode: isPlusCode ? address || '' : '',
      city: isPlusCode ? '' : city,
      state: isPlusCode ? '' : state,
      zip: isPlusCode ? '' : zip,
      visibility,
      locationDetails,
      locationIsAddress,
      customAddressType,
      locationAddressType: customAddressType,
      contactName,
      contactEmail,
      contactPhoneNumber,
      description,
      instructions,
      pointOfContactPersons,
      groups: opportunityTemplateGroups.map(({ group, associationType, approvedMembersOnly }) => ({
        groupId: group.id,
        name: group.name,
        associationType: associationType,
        approvedMembersOnly: approvedMembersOnly,
        closed: group.closed,
      })),
      questionnaires: opportunityTemplateSurveys.map(({ survey: { questionSetName, id } }) => ({
        name: questionSetName,
        id,
      })),
      waivers: opportunityTemplateCredentials.map(({ credential: { id, name } }) => ({ name, id })),
    },
    isNull,
  )

  values.useCustomAddress = !namedLocations.some(namedLocation => namedLocation.id === organizationLocationId)
  values.organizationLocationId = organizationLocationId

  return values
}

export const getLocationFormValuesFromOpportunityTemplate = (
  opportunityTemplate: OpportunityTemplate,
  namedLocations: OrganizationLocationReporting[],
): Partial<FormData> =>
  pick(
    getFormValuesFromOpportunityTemplate({
      opportunityTemplate,
      opportunityTemplateGroups: [],
      opportunityTemplateSurveys: [],
      opportunityTemplateCredentials: [],
      namedLocations,
    }),
    [
      'useCustomAddress',
      'organizationLocationId',
      'locationDetails',
      'locationIsAddress',
      'customAddressType',
      'locationAddressType',
      'address',
      'city',
      'state',
      'zip',
      'plusCode',
      'geofencing',
    ],
  )

export const mapToEventGroup = (
  eventGroup: OpportunityOccurrencePersonGroup | OpportunityTemplatePersonGroup,
): EventGroup => ({
  groupId: eventGroup.group.id,
  name: eventGroup.group.name,
  associationType: eventGroup.associationType,
  approvedMembersOnly: eventGroup.approvedMembersOnly,
  closed: eventGroup.group.closed,
})

export const mapFormDataToOpportunityPayload = (values: FormData): OpportunityPayload => {
  const {
    startDate,
    startTime,
    endDate,
    endTime,
    geofencing,
    groups,
    address,
    city,
    state,
    zip,
    locationDetails,
    locationIsAddress,
    opportunityTemplate,
    // event-specific, not used for opportunity
    recurringType,
    roles,
    contactEmail,
    contactName,
    contactPhoneNumber,
    instructions,
    registrationMessage,
    followUpEnabled,
    followUpMessage,
    followUpQuestionnaireIds,
    questionnaires,
    waivers,
    timeShiftId,
    // end event-specific, not used for opportunity
    ...data
  } = values
  const start = moment(startDate as Date)
  const end = moment(endDate as Date)
  const locationPayload = buildCustomLocationPayload(values)
  return {
    ...data,
    ...locationPayload,
    startsAt: moment(startTime as Date)
      .year(start.year())
      .dayOfYear(start.dayOfYear())
      .toDate(),
    endsAt: moment(endTime as Date)
      .year(end.year())
      .dayOfYear(end.dayOfYear())
      .toDate(),
    geofencing: mapGeofencingForLocationPayload(geofencing, values.useCustomAddress, values.customAddressType),
    groups,
    ...(opportunityTemplate ? { opportunityTemplateId: opportunityTemplate.id } : {}),
    isSchedulable: false,
  }
}

export interface MapFormDataToEventPayloadParams {
  values: FormData
  asDraft?: boolean
  startDateChanged?: boolean
}

export const useMapFormDataToEventPayload = (roles: Role[]) => {
  const { compileDateTime, unfakeAsLocalTimeForUI } = useDateTimeUtils()

  const isQuestionnaireAsSurveyEnabled = useFeatureEnabled('QuestionnaireAsSurvey')
  const isAllowMultipleRegistrationsPerVolunteerEnabled = useFeatureEnabled('AllowMultipleRegistrationsPerVolunteer')
  const isSimplifyRecurringEventRulesEnabled = useFeatureEnabled('SimplifyRecurringEventRules')
  const isEventAutomatedMessagesEnabled = useFeatureEnabled('EventAutomatedMessages')
  const timeZone = useTimeZone()

  return useCallback(
    ({ values, asDraft = false, startDateChanged = false }: MapFormDataToEventPayloadParams): EventPayload => {
      const {
        name,
        description,
        visibility,
        startDate,
        startTime,
        endDate,
        endTime,
        geofencing,
        groups,
        address,
        city,
        state,
        zip,
        locationDetails,
        locationIsAddress,
        opportunityTemplate,
        recurringType,
        roles: formRoles,
        contactEmail,
        contactName,
        contactPhoneNumber,
        instructions,
        registrationMessage,
        followUpEnabled,
        followUpMessage,
        followUpQuestionnaireIds,
        questionnaires,
        waivers,
        isTest,
        timeShiftId,
        timeShiftName,
        editMode,
        organizationLocationId,
        pointOfContactPersons,
        dates,
        simpleRecurringType,
        monthlyRecurringType,
        recurrencePattern,
        isRecurring,
        useCustomPublishTime,
        publishDate,
        publishTime,
        useCustomAddress,
        customAddressType,
        allowMultipleRegistrations,
      } = values
      let start: moment.Moment
      let end: moment.Moment
      if (isSimplifyRecurringEventRulesEnabled) {
        if (recurrencePattern) {
          const ruleDates = rrulestr(recurrencePattern).all()
          start = moment(first(ruleDates))
          end = moment(last(ruleDates))
        } else if (isEmpty(dates)) {
          start = moment(startDate as Date)
          end = moment(endDate as Date)
        } else {
          start = moment(first(dates))
          end =
            simpleRecurringType === SIMPLE_RECURRING_TYPE.SELECTED_DATES ? moment(last(dates)) : moment(endDate as Date)
        }
      } else {
        start = moment(startDate as Date)
        end = moment(endDate as Date)
      }
      const startsAt = moment
        .tz(
          moment(startTime as Date)
            .year(start.year())
            .dayOfYear(start.dayOfYear())
            .format('YYYY-MM-DD HH:mm:ss'),
          timeZone,
        )
        .toDate()
      const endsAt = moment
        .tz(
          moment(endTime as Date)
            .year(end.year())
            .dayOfYear(end.dayOfYear())
            .format('YYYY-MM-DD HH:mm:ss'),
          timeZone,
        )
        .toDate()

      const locationPayload = useCustomAddress
        ? buildCustomLocationPayload(values)
        : {
            address: null,
            city: null,
            state: null,
            zip: null,
            locationDetails: null,
            locationIsAddress: true,
            locationAddressType: LocationAddressType.AddressAndCoords,
          }

      const result: EventPayload = {
        is_recurring: false,
        name,
        startsAt,
        endsAt,
        description,
        isTest,
        instructions,
        ...(isEventAutomatedMessagesEnabled
          ? {
              registration_message: registrationMessage,
              follow_up_enabled: followUpEnabled,
              follow_up_message: followUpMessage,
              follow_up_questionnaire_ids: followUpQuestionnaireIds,
            }
          : {}),
        contact_name: contactName,
        contact_email: contactEmail,
        contact_phone_number: contactPhoneNumber,
        ...locationPayload,
        geofencing: mapGeofencingForLocationPayload(geofencing, useCustomAddress, customAddressType),
        dates: [],
        visibility,
        questionSets: questionnaires.map(({ id, name }) => ({
          [isQuestionnaireAsSurveyEnabled ? 'surveyId' : 'questionSetId']: id,
          name,
          required: true,
        })) as unknown as (JoinQuestionSetPayload | JoinSurveyPayload)[],
        requiredCredentials: waivers.map(({ id, name }) => ({ id, title: name, requiresReview: false })),
        groups: groups.filter(group => !group.lockedTag),
        timeshifts: [
          {
            ...(timeShiftId ? { id: timeShiftId } : {}),
            time_start: moment(startTime).format('h:mm A'),
            time_end: moment(endTime).format('h:mm A'),
            name: timeShiftName || 'Default',
            roles: formRoles
              .filter(({ roleId }) => roleId)
              .map(({ roleId, quantity }) => ({
                id: roleId,
                available: quantity,
                name: roles.find(({ id }) => id === roleId)!.name,
              })),
          },
        ],
        ...(opportunityTemplate ? { opportunityTemplateId: opportunityTemplate.id } : {}),
        isSchedulable: true,
        asDraft,
        editMode,
        organizationLocationId,
        pointOfContactPersonIds: pointOfContactPersons?.map(({ id }) => id),
        ...(startDateChanged ? { moveToDate: startDate as Date } : {}),
        ...(!useCustomPublishTime
          ? { forceVisibleAt: null }
          : {
              forceVisibleAt: compileDateTime(
                unfakeAsLocalTimeForUI(publishDate as Date),
                unfakeAsLocalTimeForUI(publishTime as Date),
              ).toDate(),
            }),
        allowMultipleRegistrations: getEffectiveAllowMultipleRegistrations(
          isAllowMultipleRegistrationsPerVolunteerEnabled,
          allowMultipleRegistrations,
        ),
      }

      if (isSimplifyRecurringEventRulesEnabled) {
        result.is_recurring = !isUndefined(isRecurring)
          ? isRecurring
          : simpleRecurringType !== SIMPLE_RECURRING_TYPE.SELECTED_DATES || dates.length > 1
        result.recurrencePattern = !isUndefined(recurrencePattern)
          ? recurrencePattern
          : getRecurrencePattern({
              startDate: startsAt,
              endDate: endsAt,
              dates,
              simpleRecurringType,
              monthlyRecurringType,
            })
      } else {
        result.recurring_type = recurringType
        result.is_recurring = recurringType !== RECURRING_TYPE.NEVER
        result.recurrenceInfo = {
          type: recurringType,
          startDate: startsAt,
          endDate: endsAt,
          timeZone,
        }
      }

      return result
    },
    [
      roles,
      compileDateTime,
      unfakeAsLocalTimeForUI,
      isQuestionnaireAsSurveyEnabled,
      isAllowMultipleRegistrationsPerVolunteerEnabled,
      isSimplifyRecurringEventRulesEnabled,
      isEventAutomatedMessagesEnabled,
      timeZone,
    ],
  )
}

export function getRecurrencePattern({
  startDate,
  endDate,
  dates,
  simpleRecurringType,
  monthlyRecurringType,
}: RecurrencePatternProps): string {
  switch (simpleRecurringType) {
    case SIMPLE_RECURRING_TYPE.WEEKLY:
      return new RRule({
        freq: RRule.WEEKLY,
        byweekday: dates.map(date => RRule[dayjs(date).format('dd').toUpperCase() as keyof typeof RRule]) as Weekday[],
        dtstart: startDate,
        until: endDate,
      }).toString()
    case SIMPLE_RECURRING_TYPE.MONTHLY:
      if (monthlyRecurringType === MONTHLY_RECURRING_TYPE.RESPECT_DATE) {
        return new RRule({
          freq: RRule.MONTHLY,
          bymonthday: dates.map(date => date.getDate()),
          dtstart: startDate,
          until: endDate,
        }).toString()
      } else {
        const weekdays = new Map<string, number[]>()
        for (const date of dates) {
          const weekday = dayjs(date).format('dd').toUpperCase()
          const weekdayNumber = getWeekdayNumberInMonth(date)
          if (!weekdays.has(weekday)) weekdays.set(weekday, [])
          weekdays.get(weekday)!.push(weekdayNumber)
        }
        const ruleSet = new RRuleSet()
        ;[...weekdays.entries()].forEach(([weekday, positions]) => {
          ruleSet.rrule(
            new RRule({
              freq: RRule.MONTHLY,
              byweekday: [RRule[weekday as keyof typeof RRule] as Weekday],
              bysetpos: positions,
              dtstart: startDate,
              until: endDate,
            }),
          )
        })
        return ruleSet.toString()
      }
    default:
      const dateObject = dayjs(startDate)
      const ruleSet = new RRuleSet()
      dates.forEach(date =>
        ruleSet.rdate(dateObject.year(date.getFullYear()).month(date.getMonth()).date(date.getDate()).toDate()),
      )
      return ruleSet.toString()
  }
}
export const getInitialEditMode = (event: OpportunityResponseWithRecurrenceInfo) => {
  return event.status === OpportunityStatus.Draft ? 'all' : 'single'
}

export const toLocalDate = (localTime: string | null): Date | null =>
  !isEmpty(localTime) ? moment(localTime as string, 'LT').toDate() : null
export const toLocalTime = (localDate: Date | null): string | null =>
  localDate ? moment(localDate).format('LT') : null
